import { useCallback, useMemo, useState } from 'react';
import { calculatePdfPagination } from '../utils.js/utils';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from './pdfEngine';

/**
 * Shared PDF export state machine.
 *
 * Owns: preview modal state, single-record preview state, chunk pagination,
 * generation flags — and the print handlers. Pages provide only data + config.
 *
 * Usage:
 *   const pdf = usePdfExport({ idPrefix: 'taxes', isRTL, fileNamePrefix: 'ACE_Taxes' });
 *   pdf.previewAll(rows) / pdf.printAll(rows) / pdf.previewSingle(row) / pdf.printSingle(row)
 *   <PdfExportManager pdf={pdf} columns={...} title={...} ... />
 */
const usePdfExport = ({
  idPrefix,
  isRTL = true,
  fileNamePrefix = 'ACE_Report',
  onError,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [singleRecord, setSingleRecord] = useState(null);
  const [printMode, setPrintMode] = useState(null); // null | 'all' | 'single'

  const buildChunks = useCallback(
    rows => {
      const topHeader = document.getElementById(
        `${idPrefix}-pdf-measure-top-header`,
      );
      const thead = document.getElementById(`${idPrefix}-pdf-measure-thead`);
      const footer = document.getElementById(`${idPrefix}-pdf-measure-footer`);
      const tbody = document.getElementById(`${idPrefix}-pdf-measure-tbody`);
      return calculatePdfPagination(rows, { topHeader, thead, footer, tbody });
    },
    [idPrefix],
  );

  const reportError = useCallback(
    err => {
      if (onError) onError(err);
    },
    [onError],
  );

  /** Open the multi-page preview modal for the given rows. */
  const previewAll = useCallback(
    rows => {
      if (!rows || rows.length === 0) {
        reportError(new Error('NO_DATA'));
        return false;
      }
      setPdfChunks(buildChunks(rows));
      setIsPreviewOpen(true);
      return true;
    },
    [buildChunks, reportError],
  );

  /** Generate + download the multi-page PDF (same DOM as the preview). */
  const printAll = useCallback(
    async rows => {
      if (!rows || rows.length === 0) {
        reportError(new Error('NO_DATA'));
        return;
      }
      try {
        const chunks = buildChunks(rows);
        setPdfChunks(chunks);
        setPrintMode('all');
        await waitForRender();
        setIsGeneratingPdf(true);
        await generatePdfFromElementIds({
          elementIds: chunks.map((_, i) => `${idPrefix}-pdf-page-${i}`),
          fileName: datedFileName(fileNamePrefix),
          isRTL,
        });
      } catch (err) {
        reportError(err);
      } finally {
        setIsGeneratingPdf(false);
        setPrintMode(null);
      }
    },
    [buildChunks, idPrefix, fileNamePrefix, isRTL, reportError],
  );

  /** Open the single-record preview modal. */
  const previewSingle = useCallback(
    record => {
      if (!record) {
        reportError(new Error('NO_DATA'));
        return false;
      }
      setSingleRecord(record);
      setIsSinglePreviewOpen(true);
      return true;
    },
    [reportError],
  );

  /** Generate + download a single-record PDF (same engine, same layout). */
  const printSingle = useCallback(
    async (record, fileNameSuffix) => {
      if (!record) {
        reportError(new Error('NO_DATA'));
        return;
      }
      try {
        setSingleRecord(record);
        setPrintMode('single');
        await waitForRender();
        setIsGeneratingPdf(true);
        const suffix = fileNameSuffix ? `_${fileNameSuffix}` : '';
        await generateSingleElementPdf({
          elementId: `${idPrefix}-pdf-single`,
          fileName: datedFileName(`${fileNamePrefix}${suffix}`),
          isRTL,
        });
      } catch (err) {
        reportError(err);
      } finally {
        setIsGeneratingPdf(false);
        setPrintMode(null);
        // NOTE: singleRecord is intentionally kept — the single preview modal
        // may still be open; closeSinglePreview() clears it.
      }
    },
    [idPrefix, fileNamePrefix, isRTL, reportError],
  );

  const closePreview = useCallback(() => setIsPreviewOpen(false), []);
  const closeSinglePreview = useCallback(() => {
    setIsSinglePreviewOpen(false);
    setSingleRecord(null);
  }, []);

  return useMemo(
    () => ({
      idPrefix,
      isRTL,
      // state
      isPreviewOpen,
      isSinglePreviewOpen,
      isGeneratingPdf,
      pdfChunks,
      singleRecord,
      printMode,
      // actions
      previewAll,
      printAll,
      previewSingle,
      printSingle,
      closePreview,
      closeSinglePreview,
    }),
    [
      idPrefix,
      isRTL,
      isPreviewOpen,
      isSinglePreviewOpen,
      isGeneratingPdf,
      pdfChunks,
      singleRecord,
      printMode,
      previewAll,
      printAll,
      previewSingle,
      printSingle,
      closePreview,
      closeSinglePreview,
    ],
  );
};

export default usePdfExport;
